import type { CSSProperties } from 'react';
import { type ComponentPropsWithoutRef, type RefObject, useRef } from 'react';
import styled, { css } from 'styled-components';

import { hiddenScrollbar } from '@/styles/style';
import keymap from '@/utils/keymap';
import { stopEvent } from '@/utils/util';

import useFlexibleTextarea from './useFlexibleTextarea';

type Props = ComponentPropsWithoutRef<'textarea'> & {
  initFocus?: boolean;
  style?: CSSProperties;
  minHeight?: number;
  showBorder?: boolean;
  showBg?: boolean;
};

const FlexibleTitleInput = (props: Props) => {
  const ref = useRef(null) as RefObject<HTMLTextAreaElement>;

  useFlexibleTextarea(
    {
      initFocus: props.initFocus,
      value: props.value as string,
      minHeight: props.minHeight,
    },
    ref,
  );

  return (
    <Style
      {...props}
      rows={1}
      ref={ref}
      onKeyUp={stopEvent}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.nativeEvent.isComposing) return;
        if (keymap.isEscape(e)) {
          ref.current?.blur();
        }
        props.onKeyDown?.(e);
      }}
    />
  );
};

export const Style = styled.textarea<Props>`
  line-height: 1.3;
  color: ${(props) => props.theme.colors.gray950};
  outline: none;
  border: 1px solid transparent;
  background-color: transparent;
  border-radius: 4px;
  transition:
    background-color 0.2s,
    border-color 0.2s;
  resize: none;
  width: 100%;
  height: 24px;
  padding: 8px 12px;
  font-size: 20px;
  font-weight: 600;

  ${hiddenScrollbar};

  &:hover {
    background-color: ${(props) => props.theme.colors.gray250};
  }

  &:focus {
    border: 1px solid ${(props) => props.theme.colors.gray500};
    background-color: ${(props) => props.theme.colors.gray250};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.gray500};
  }

  ${({ showBorder }) =>
    showBorder &&
    css`
      border: 1px solid ${(props) => props.theme.colors.gray300};
    `}
  ${({ showBg = true }) =>
    showBg &&
    css`
      background-color: ${(props) => props.theme.colors.FD04};
    `}
`;

export default FlexibleTitleInput;
